import { AxiosResponse } from 'axios';

import { ExecutionStatus, ExecutionStatusColor } from '@qality/common-model';
import { AxiosWrapperService as axios } from '@qality/common-service';

import StorageOfUniqueServices from './StorageOfUniqueServices';

export type ExecutionStatuses = {
  allStatuses: Array<ExecutionStatus>;
  defaultStatus: ExecutionStatus;
  colors?: Array<ExecutionStatusColor>;
};

type GetStatusesResponseData = {
  statuses: Array<ExecutionStatus>;
  colors: Array<ExecutionStatusColor> | null;
};

const headers = {
  'Content-Type': 'application/json',
};

const getStatuses = async (
  params?: Partial<{
    showColors: boolean;
  }>
): Promise<ExecutionStatuses> => {
  const finalParams: Required<typeof params> = {
    showColors: params?.showColors ?? false,
  };

  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_EXECUTION_STATUSES,
    Object.entries(finalParams).map(([key, value]) => `${key}=${value}`)
  );

  const response = await axios.get(url);

  const data = response.data as GetStatusesResponseData;

  return {
    allStatuses: data.statuses,
    defaultStatus: data.statuses.find((s) => s.isDefault),
    colors: data.colors,
  };
};

const createStatus = async (): Promise<ExecutionStatus> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_EXECUTION_STATUSES
  );

  const response = await axios.post(url, null, {
    headers: {
      'Content-Type': 'application/json',
    },
  });

  return response.data;
};

const updateStatus = async (
  executionStatus: ExecutionStatus
): Promise<void> => {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_EXECUTION_STATUSES +
      `/${executionStatus.id}`
  );

  const data = getPatchUpdateStatusData(executionStatus);

  axios.patch(url, data, { headers });
};

const deleteStatus = async (
  statusToDeleteId: number,
  alternativeStatusId: number
): Promise<AxiosResponse<string>> => {
  const alternativeIdParam = {
    alternativeStatusId,
  };
  const URL_PARAMS = Object.entries(alternativeIdParam).map(
    ([key, value]) => `${key}=${value}`
  );

  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_EXECUTION_STATUSES +
      `/${statusToDeleteId}`,
    URL_PARAMS
  );

  return axios.delete(url);
};

const getPatchUpdateStatusData = (status: ExecutionStatus) => {
  return {
    fields: {
      ...(status.name ? { name: status.name } : {}),
      ...(status.isDefault ? { isDefault: status.isDefault } : {}),
      ...(status.color ? { colorName: status.color.name } : {}),
      ...(status.category ? { category: status.category } : {}),
      ...(status.position ? { position: status.position } : {}),
    },
  };
};

export default {
  getStatuses,
  updateStatus,
  deleteStatus,
  createStatus,
};
